import React, { useState, useEffect, ChangeEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import uuid from 'uuid'
import {
  closeCarrierCapacityDialog,
  editCarrierCapacity,
} from 'apps/carrier/utils/actions/capacity'
import { IStore } from 'apps/carrier/utils/reducers'
import { ICarrierCapacity } from 'apps/marketplace/store/contractLanes/types'
import { FormModal } from 'transfix-ui'
import { lookupCountryByStateCode } from 'utils/getLocations'
import { CapacityEntryForm } from './CapacityEntryForm'
import { CapacityEntryList } from './CapacityEntryList'

interface IState {
  id?: string
  offerLimits: ICarrierCapacity[]
  showDialog: boolean
  updating: boolean
  error?: string
}

export interface ILocation {
  city: string
  state: string
}

export enum DAY_LIMITS {
  Mon = 'mondayLimit',
  Tue = 'tuesdayLimit',
  Wed = 'wednesdayLimit',
  Thu = 'thursdayLimit',
  Fri = 'fridayLimit',
  Sat = 'saturdayLimit',
  Sun = 'sundayLimit',
}

export type dayLimitsKey = keyof typeof DAY_LIMITS

const defaultCarrierCapacity: ICarrierCapacity = {
  loading: false,
  pickupLocation: {
    stateCode: '',
    countryCode: '',
    city: '',
    radius: 25,
  },
  mondayLimit: '0',
  tuesdayLimit: '0',
  wednesdayLimit: '0',
  thursdayLimit: '0',
  fridayLimit: '0',
  saturdayLimit: '0',
  sundayLimit: '0',
  uuid: '',
  pickupLocationError: '',
}

export const CapacityDialog = () => {
  const dispatch = useDispatch()
  const { error, id, offerLimits, showDialog, updating } = useSelector<IStore, IState>(
    ({ carrier, carrierCapacity }) => ({
      id: carrier.carrierDetails?.id,
      offerLimits: carrierCapacity.capacities,
      showDialog: carrierCapacity.showDialog,
      updating: carrierCapacity.updating,
      error: carrierCapacity.updateError,
    })
  )
  const closeDialogHandler = () => dispatch(closeCarrierCapacityDialog())

  const [localCapacities, setLocalCapacities] = useState<ICarrierCapacity[]>([])
  const [newLocalCapacity, setNewLocalCapacity] = useState<ICarrierCapacity>(defaultCarrierCapacity)

  const saveCarrierCapacity = () => {
    const flattenCapacities = localCapacities
      .filter(v => !(v._destroy && !v.id))
      .map(localCapacity => ({
        ...localCapacity,
        pickupCity: localCapacity.pickupLocation?.city,
        pickupStateCode: localCapacity.pickupLocation?.stateCode,
        pickupRadius: localCapacity.pickupLocation?.radius,
        pickupCountryCode: localCapacity.pickupLocation?.countryCode,
      }))
    if (id) dispatch(editCarrierCapacity(id, flattenCapacities))
  }

  useEffect(() => {
    if (showDialog && offerLimits.length > 0) {
      setLocalCapacities([...offerLimits])
    }
  }, [showDialog, offerLimits])

  const updateNewLocation = ({ city, state }: ILocationSuggestion) => {
    const countryCode = lookupCountryByStateCode(state)
    setNewLocalCapacity({
      ...newLocalCapacity,
      pickupLocation: {
        ...newLocalCapacity.pickupLocation,
        countryCode: countryCode ? countryCode.code : 'US',
        stateCode: state ?? '',
        city: city ?? '',
      },
      pickupLocationError: '',
    })
  }

  const updateNewRadius = ({ value }: { value: number }) => {
    setNewLocalCapacity({
      ...newLocalCapacity,
      pickupLocation: {
        ...newLocalCapacity.pickupLocation,
        radius: Number(value),
      },
    })
  }

  const updateNewDayLimit = ({ target: { id: dayId, value } }: ChangeEvent<HTMLInputElement>) => {
    const dayType = dayId as dayLimitsKey
    setNewLocalCapacity({
      ...newLocalCapacity,
      [DAY_LIMITS[dayType]]: Number(value),
    })
  }

  const deleteCapacity = (uuidMatch: string) => {
    const updatedLocalCapacity = localCapacities.map((capacity: ICarrierCapacity) => ({
      ...capacity,
      _destroy: capacity.uuid === uuidMatch,
    }))
    setLocalCapacities(updatedLocalCapacity)
  }

  const addLocalCapacity = () => {
    if (newLocalCapacity.pickupLocation?.city && newLocalCapacity.pickupLocation?.stateCode) {
      setLocalCapacities([...localCapacities, { ...newLocalCapacity, uuid: uuid() }])
      setNewLocalCapacity(defaultCarrierCapacity)
    } else {
      setNewLocalCapacity({ ...newLocalCapacity, pickupLocationError: 'Please select a location' })
    }
  }

  return (
    <FormModal
      title='Edit Capacity'
      size={7}
      open={showDialog}
      onClose={closeDialogHandler}
      error={error}
      dataTest='modal-edit-capacity'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: updating,
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: updating,
          action: saveCarrierCapacity,
        },
      ]}>
      <CapacityEntryForm
        capacity={newLocalCapacity}
        updateLocation={updateNewLocation}
        updateRadius={updateNewRadius}
        updateDayLimit={updateNewDayLimit}
        addCapacity={addLocalCapacity}
      />
      <hr />
      <CapacityEntryList
        localCapacities={localCapacities}
        setLocalCapacities={setLocalCapacities}
        deleteCapacity={deleteCapacity}
      />
    </FormModal>
  )
}
